import { Component, Inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { NzCheckBoxOptionInterface } from 'ng-zorro-antd/checkbox';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { TYPE } from '../../../hezky-routes';
import { finalize } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-generic-filter-drawer',
  templateUrl: './generic-filter-drawer.component.html',
})
export class GenericFilterDrawerComponent {
  form: FormGroup = new FormGroup({});
  isLoading: boolean = false;
  pSelectOptions: { [key: string]: any[] } = {};
  ngOnInit(): void {
    this.data.fields.forEach((field: TYPE.filterField) => {
      const validators = field.validatorFn || [];
      if (field.type === 'checkbox') this.form.addControl(field.name, new FormControl(field.options, validators));
      else if (field.type === 'select') this.form.addControl(field.name, new FormControl(field.multiple ? [] : null, validators));
      else if (field.type === 'p-select') {
        this.form.addControl(field.name, new FormControl(null, validators));
        this.loadMore(field);
      }
      else this.form.addControl(field.name, new FormControl(null, validators));
    });

    if (Object.keys(this.filterData).length > 0) {
      this.data.fields.forEach((field: TYPE.filterField) => {
        if (field.type !== 'checkbox') {
          this.form.get(field.name)?.patchValue(this.filterData[field.name]);
        }
        if(field?.type === 'select' && !field?.multiple && Array.isArray(this.filterData[field.name])) {
          this.form.get(field.name)?.patchValue(this.filterData[field.name][0]);
        }
      });
    }
  }

  protected updateArrayObjects(array1: NzCheckBoxOptionInterface[], array2: NzCheckBoxOptionInterface[]): NzCheckBoxOptionInterface[] {
    const map = new Map(array2.map(item => [item.value, item]));
    return array1.map(item => map.get(item.value) || item);
  }

  protected getErrorMessage(fieldName: string, field: TYPE.filterField): string {
    const control = this.form.get(fieldName);
    if (!control?.errors) return '';
    const e = control.errors;
    const msg = field.validationMessages || {};
    if (e['required']) return msg['required'] || 'This field is required';
    if (e['min']) return msg['min'] || `Minimum value is ${e['min'].min}`;
    if (e['max']) return msg['max'] || `Maximum value is ${e['max'].max}`;
    if (e['minlength']) return msg['minlength'] || `Minimum ${e['minlength'].requiredLength} characters`;
    if (e['maxlength']) return msg['maxlength'] || `Maximum ${e['maxlength'].requiredLength} characters`;
    if (e['pattern']) return msg['pattern'] || 'Invalid format';
    const firstKey = Object.keys(e)[0];
    return msg[firstKey] || 'Invalid value';
  }

  protected applyHandler(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    let emitData: any = {};
    this.data.fields.forEach((field: TYPE.filterField) => {
      if (field.type === 'checkbox') emitData[field.name] = this.form.get(field.name)?.value?.length > 0 ? this.form.get(field.name)?.value.filter((opt: NzCheckBoxOptionInterface) => opt.checked).map((opt: NzCheckBoxOptionInterface) => opt.value) : [];
      else if (field.type === 'select') emitData[field.name] = this.form.get(field.name)?.value;
      else emitData[field.name] = this.form.get(field.name)?.value;
    });
    for (const element of Object.keys(emitData)) {
      if (emitData[element]?.length === 0) delete emitData[element];
      if (emitData[element] === '') delete emitData[element];
      if (emitData[element] === null) delete emitData[element];
      if (emitData[element] === undefined) delete emitData[element];
    }
    this.drawerRef.close(emitData);
  }

  protected resetHandler(): void {
    for (const field of this.data.fields) {
      if (field.type === 'checkbox' && Array.isArray(field.options)) {
        for (const option of field.options) {
          option.checked = false;
        }
        this.form.get(field.name)?.setValue(field.options, { emitEvent: false });
      } else if (field.type === 'select') {
        const resetValue = field.multiple ? [] : null;
        this.form.get(field.name)?.setValue(resetValue, { emitEvent: false });
      } else {
        this.form.get(field.name)?.reset('', { emitEvent: false });
      }
    }
  }
  pSelectNoMoreData: WeakMap<any, boolean> = new WeakMap<any, boolean>();
  protected loadMore(field: TYPE.filterField): void {
    const pSelectField = field as TYPE.filterFieldPSelect;
    if (!this.pSelectNoMoreData) {
      this.pSelectNoMoreData = new WeakMap<any, boolean>();
    }
    if (this.pSelectNoMoreData.get(pSelectField)) {
      this.isLoading = false;
      return;
    }
    if (pSelectField.type === 'p-select' && pSelectField?.service) {
      this.isLoading = true;
      const currentLimit = pSelectField.payload[pSelectField.limitText] || 0;
      const nextLimit = currentLimit + 1;
      const payload = {
        ...pSelectField.payload,
        [pSelectField.limitText]: nextLimit
      };
      pSelectField.payload[pSelectField.limitText] = nextLimit;
      pSelectField.service()[pSelectField.method](payload)
        .pipe(untilDestroyed(this), finalize(() => this.isLoading = false))
        .subscribe((res: any) => {
          if (res?.status !== 200 || !res?.data || !Array.isArray(res.data[pSelectField.listName])) {
            this.pSelectNoMoreData.set(pSelectField, true);
            return;
          }
          const list = res.data[pSelectField.listName] as any[];
          if (!list.length) {
            this.pSelectNoMoreData.set(pSelectField, true);
            return;
          }
          const newOptions = list.map(item => ({
            value: item[pSelectField.nzValue],
            label: pSelectField.nzLabel.map(label => item[label]).join(' - ')
          }));
          const existingOptions = this.pSelectOptions[pSelectField.name] || [];
          const existingValues = new Set(existingOptions.map((opt: any) => opt.value));
          const mergedOptions = [
            ...existingOptions,
            ...newOptions.filter(opt => !existingValues.has(opt.value))
          ];
          this.pSelectOptions[pSelectField.name] = mergedOptions;
        });
    }
  }

  constructor(
    protected readonly drawerRef: NzDrawerRef<string>,
    @Inject(NZ_DRAWER_DATA) public data: TYPE.filterConfig,
    @Inject(NZ_DRAWER_DATA) public filterData: any,
  ) { }
}
